/**
 * Review Moment recording only — do not snap the live stream range.
 *
 * Mount stores an inner `doc.slice` (closed inlines, no wrapper). Undo of
 * that on the textblock node wraps the inlines in a paragraph, so the
 * original wrapper is lost.
 *
 * Snap both sides to the enclosing textblock so undo replaces node with
 * node. `reviewSlicesForReplaceRange` keeps the mount wrapper if restore
 * already lifted those inlines to a paragraph.
 */
import { Fragment, Slice, type Node as PMNode } from '@atlaskit/editor-prosemirror/model';

export const enclosingTextblockRange = (
	doc: PMNode,
	from: number,
	to: number,
): { from: number; to: number } | null => {
	if (from < 0 || to < from || to > doc.content.size) {
		return null;
	}

	const nodeAtFrom = from < doc.content.size ? doc.nodeAt(from) : null;
	if (nodeAtFrom?.isTextblock) {
		const nodeEnd = from + nodeAtFrom.nodeSize;
		if (to > nodeEnd) {
			return null;
		}
		return { from, to: nodeEnd };
	}

	const $from = doc.resolve(from);
	if (!$from.parent.isTextblock) {
		return null;
	}

	const wrapFrom = $from.before($from.depth);
	const wrapTo = $from.after($from.depth);
	if (to > wrapTo) {
		return null;
	}
	return { from: wrapFrom, to: wrapTo };
};

export const slicesMatchingReplaceRange = ({
	from,
	newDoc,
	originalDoc,
	to,
}: {
	from: number;
	newDoc: PMNode;
	originalDoc: PMNode;
	to: number;
}): { endPos: number; newSlice: Slice; originalSlice: Slice; startPos: number } => {
	const snap = enclosingTextblockRange(originalDoc, from, to) ?? { from, to };
	const startPos = snap.from;
	const endPos = Math.max(
		startPos,
		Math.min(snap.to + (newDoc.content.size - originalDoc.content.size), newDoc.content.size),
	);
	return {
		endPos,
		newSlice: newDoc.slice(startPos, endPos),
		originalSlice: originalDoc.slice(snap.from, snap.to),
		startPos,
	};
};

const textblockInSlice = (slice: Slice): PMNode | null => {
	if (slice.content.childCount !== 1) {
		return null;
	}
	let node: PMNode | null = slice.content.firstChild;
	while (node && !node.isTextblock && node.childCount === 1) {
		node = node.firstChild;
	}
	return node?.isTextblock ? node : null;
};

const closedTextblockSlice = (node: PMNode): Slice => new Slice(Fragment.from(node), 0, 0);

/**
 * Review recording: same pairing as `slicesMatchingReplaceRange`, but keep the
 * mount-time textblock when restore already replaced it with a paragraph.
 * Only when the new content is still that same textblock type — a heading
 * that became a list must not force the heading back onto the live range.
 */
export const reviewSlicesForReplaceRange = ({
	from,
	newDoc,
	originalDoc,
	reviewOriginalSlice,
	to,
}: {
	from: number;
	newDoc: PMNode;
	originalDoc: PMNode;
	reviewOriginalSlice?: Slice;
	to: number;
}): { endPos: number; newSlice: Slice; originalSlice: Slice; startPos: number } => {
	const live = slicesMatchingReplaceRange({ from, newDoc, originalDoc, to });
	const mountTextblock = reviewOriginalSlice ? textblockInSlice(reviewOriginalSlice) : null;
	if (!mountTextblock || !reviewOriginalSlice) {
		return live;
	}

	// Restore the mount wrapper only if streaming actually changed its markup.
	// Otherwise the live slices already cover the whole replacement and retain
	// edits made since mount, including edits outside a partial replacement.
	if (textblockInSlice(live.originalSlice)?.sameMarkup(mountTextblock)) {
		return live;
	}

	const newRange =
		enclosingTextblockRange(newDoc, live.startPos, live.endPos) ??
		(live.startPos < live.endPos
			? enclosingTextblockRange(newDoc, live.startPos + 1, Math.min(live.endPos, live.startPos + 1))
			: null);
	const newTextblock = newRange ? newDoc.nodeAt(newRange.from) : null;
	if (!newRange || !newTextblock?.isTextblock || newTextblock.type !== mountTextblock.type) {
		return live;
	}

	const originalSlice =
		reviewOriginalSlice.content.firstChild?.type === mountTextblock.type
			? reviewOriginalSlice
			: closedTextblockSlice(mountTextblock);

	return {
		endPos: newRange.to,
		newSlice: newDoc.slice(newRange.from, newRange.to),
		originalSlice,
		startPos: newRange.from,
	};
};
